import { format } from 'date-fns';

const INSTAGRAM_API_BASE = 'https://graph.instagram.com/v24.0';

export interface InstagramMedia {
  id: string;
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  media_url: string;
  thumbnail_url?: string;
  caption?: string;
  permalink: string;
  timestamp: string;
  /**
   * Поля ниже приходят в том же ответе, что и список — бесплатно. Раньше за
   * каждым из них ходили отдельным запросом на публикацию: media_product_type
   * ради выбора метрик, like_count/comments_count ради лайков и комментариев.
   * На сотне публикаций это двести лишних последовательных обращений.
   */
  media_product_type?: string;
  like_count?: number;
  comments_count?: number;
}

export interface MediaInsights {
  permalink: string;
  media_type: string;
  shares?: number;
  comments?: number;
  likes?: number;
  views?: number;
  saved?: number;
  reach?: number;
  total_interactions?: number;
  crossposted_views?: number;
  facebook_views?: number;
}

/** Базовые поля списка публикаций — их Instagram отдаёт всегда. */
export const MEDIA_FIELDS_BASE = 'id,media_type,media_url,thumbnail_url,caption,permalink,timestamp';

/**
 * Эти три поля приходят вместе со списком и экономят по два запроса на каждую
 * публикацию. Но Graph на отказ по одному полю валит запрос целиком, поэтому
 * первый неудачный ответ мы повторяем без них: статистика тогда собирается по
 * старой, медленной схеме, но собирается.
 */
export const MEDIA_FIELDS_EXTRA = 'media_product_type,like_count,comments_count';

export function mediaListUrl(
  userId: string,
  accessToken: string,
  limit: number,
  withExtras: boolean,
  window = '',
): string {
  const fields = withExtras ? `${MEDIA_FIELDS_BASE},${MEDIA_FIELDS_EXTRA}` : MEDIA_FIELDS_BASE;
  return `${INSTAGRAM_API_BASE}/${userId}/media?fields=${fields}&limit=${limit}${window}&access_token=${accessToken}`;
}

export function dateRangeWindow(fromMs: number, toMs: number): string {
  const HOUR = 3600;
  let window = '';
  if (fromMs > 0) window += `&since=${Math.floor(fromMs / 1000) - HOUR}`;
  if (Number.isFinite(toMs)) window += `&until=${Math.ceil(toMs / 1000) + HOUR}`;
  return window;
}

/**
 * Получает последние media для аккаунта с поддержкой пагинации и фильтрации по дате
 */
export async function getRecentMedia(
  accessToken: string,
  userId: string,
  limit = 50,
  dateRange?: { from?: Date; to?: Date }
): Promise<InstagramMedia[]> {
  try {
    const allMedia: InstagramMedia[] = [];
    const pageSize = Math.min(limit, 100);
    let withExtras = true;
    let pageNumber = 1;
    let totalFetched = 0;
    
    // Определяем диапазон дат для фильтрации
    const fromTimestamp = dateRange?.from ? new Date(dateRange.from).getTime() : 0;
    const toTimestamp = dateRange?.to ? new Date(dateRange.to).getTime() + 86400000 : Infinity; // +1 день для включения конечной даты

    // since/until: Instagram сразу отдаёт посты периода, а не ленту от самых
    // свежих — на прошлых месяцах это десятки секунд. Окно шире на час, края
    // отрезает фильтр ниже. Курсор paging.next окно сохраняет.
    const window = dateRangeWindow(fromTimestamp, toTimestamp);
    let url: string | null = mediaListUrl(userId, accessToken, pageSize, withExtras, window);
    
    console.log(`[getRecentMedia] Starting pagination for user ${userId}...`);
    if (dateRange?.from || dateRange?.to) {
      console.log(`[getRecentMedia] Date filter: ${dateRange.from ? format(new Date(dateRange.from), 'dd.MM.yyyy') : 'no start'} - ${dateRange.to ? format(new Date(dateRange.to), 'dd.MM.yyyy') : 'no end'}`);
    }
    
    while (url && totalFetched < limit) {
      console.log(`[getRecentMedia] Fetching page ${pageNumber}...`);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const text = await response.text();
        // Аккаунт не отдал расширенные поля — Graph отклонил весь запрос.
        // Повторяем первую страницу без них, чтобы не потерять статистику
        // целиком: следующие страницы наследуют поля из ссылки paging.next.
        if (withExtras && pageNumber === 1) {
          console.warn('[getRecentMedia] расширенные поля отклонены, повтор без них:', text);
          withExtras = false;
          url = mediaListUrl(userId, accessToken, pageSize, false, window);
          continue;
        }
        console.error('Instagram API error:', text);
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          throw new Error(`Instagram API error: ${response.status} ${response.statusText}`);
        }
        throw new Error(errorData.error?.message || 'Ошибка при получении медиа');
      }
      
      const text = await response.text();
      if (!text) {
        console.warn('Empty response from Instagram API');
        break;
      }
      
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse response:', text);
        throw new Error('Некорректный ответ от Instagram API');
      }
      
      if (!data.data || !Array.isArray(data.data)) {
        console.warn('No data array in response:', data);
        break;
      }
      
      // Фильтруем по дате если указан dateRange
      let pageMedia = data.data;
      
      if (dateRange?.from || dateRange?.to) {
        pageMedia = data.data.filter((media: InstagramMedia) => {
          const mediaTimestamp = new Date(media.timestamp).getTime();
          return mediaTimestamp >= fromTimestamp && mediaTimestamp < toTimestamp;
        });
        
        console.log(`[getRecentMedia] Page ${pageNumber}: ${data.data.length} total, ${pageMedia.length} after date filter`);
      } else {
        console.log(`[getRecentMedia] Page ${pageNumber}: ${data.data.length} media`);
      }
      
      allMedia.push(...pageMedia);
      totalFetched += data.data.length;
      
      // Проверяем, не вышли ли мы за пределы диапазона дат
      if (dateRange?.from && data.data.length > 0) {
        const oldestMediaOnPage = data.data[data.data.length - 1];
        const oldestTimestamp = new Date(oldestMediaOnPage.timestamp).getTime();
        
        // Если самый старый пост на странице старше начала периода, останавливаемся
        if (oldestTimestamp < fromTimestamp) {
          console.log(`[getRecentMedia] Reached posts older than date range. Stopping pagination.`);
          break;
        }
      }
      
      // Переходим к следующей странице
      url = data.paging?.next || null;
      pageNumber++;
      
      // Защита от бесконечного цикла - максимум 50 страниц
      if (pageNumber > 50) {
        console.warn('[getRecentMedia] Reached maximum pagination limit (50 pages)');
        break;
      }
    }
    
    console.log(`[getRecentMedia] Completed! Total pages: ${pageNumber - 1}, Total media loaded: ${allMedia.length}`);
    
    return allMedia;
  } catch (error) {
    console.error('Error in getRecentMedia:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Не удалось получить медиа');
  }
}

/**
 * Извлекает username из permalink Instagram
 */
export function extractUsernameFromPermalink(permalink: string): string | null {
  try {
    // Примеры:
    // https://www.instagram.com/qazaqstan.24/reel/DQvrSKxDBc9/
    // https://www.instagram.com/news.qaz/p/DQvp9vsDZPy/
    const match = permalink.match(/instagram\.com\/([^\/]+)\/(p|reel)\//);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Нормализует permalink - убирает username и параметры
 * https://www.instagram.com/shymda_/p/DQvrBe2jHHe/ -> https://www.instagram.com/p/DQvrBe2jHHe/
 * https://www.instagram.com/p/DQvrBe2jHHe/ -> https://www.instagram.com/p/DQvrBe2jHHe/
 */
export function normalizePermalink(permalink: string): string {
  try {
    // Удаляем trailing slash и параметры
    let normalized = permalink.replace(/\/$/, '').split('?')[0];
    
    // Если есть username, убираем его
    // https://www.instagram.com/username/p/CODE/ -> https://www.instagram.com/p/CODE/
    normalized = normalized.replace(/instagram\.com\/[^\/]+\/(p|reel)\//, 'instagram.com/$1/');
    
    return normalized;
  } catch {
    return permalink;
  }
}

/**
 * Получает все media за период для аккаунта
 */
export async function getMediaByDateRange(
  accessToken: string,
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<InstagramMedia[]> {
  try {
    const allMedia: InstagramMedia[] = [];
    let withExtras = true;
    let pagesRead = 0;
    const window = dateRangeWindow(startDate.getTime(), endDate.getTime());
    let url = mediaListUrl(userId, accessToken, 100, withExtras, window);
    
    while (url) {
      const response = await fetch(url);
      
      if (!response.ok) {
        const text = await response.text();
        // Тот же откат, что и в getRecentMedia: расширенные поля отдают не все
        // аккаунты, а отказ по одному полю валит весь запрос.
        if (withExtras && pagesRead === 0) {
          console.warn('[getMediaByDateRange] расширенные поля отклонены, повтор без них:', text);
          withExtras = false;
          url = mediaListUrl(userId, accessToken, 100, false, window);
          continue;
        }
        console.error('Instagram API error:', text);
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          throw new Error(`Instagram API error: ${response.status} ${response.statusText}`);
        }
        throw new Error(errorData.error?.message || 'Ошибка при получении медиа');
      }
      
      const text = await response.text();
      if (!text) {
        console.warn('Empty response from Instagram API');
        break;
      }
      
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse response:', text);
        throw new Error('Некорректный ответ от Instagram API');
      }
      
      if (!data.data || !Array.isArray(data.data)) {
        console.warn('No data array in response:', data);
        break;
      }
      
      // Фильтруем по дате
      const filteredMedia = data.data.filter((media: InstagramMedia) => {
        const mediaDate = new Date(media.timestamp);
        return mediaDate >= startDate && mediaDate <= endDate;
      });
      
      allMedia.push(...filteredMedia);
      pagesRead++;
      
      // Если нашли медиа старше startDate, прекращаем пагинацию
      const oldestMedia = data.data[data.data.length - 1];
      if (oldestMedia && new Date(oldestMedia.timestamp) < startDate) {
        break;
      }
      
      url = data.paging?.next || null;
    }
    
    return allMedia;
  } catch (error) {
    console.error('Error in getMediaByDateRange:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Не удалось получить медиа за период');
  }
}

/**
 * Получает insights для конкретного media
 */
export async function getMediaInsights(
  accessToken: string,
  mediaId: string,
  mediaType: string,
  /**
   * Тип публикации из списка медиа. Передан — лишнего запроса не делаем:
   * это ровно те же данные, что уже пришли вместе со списком.
   */
  knownProductType?: string
): Promise<Partial<MediaInsights>> {
  try {
    let media_type = mediaType;
    let media_product_type = knownProductType;

    if (!media_product_type) {
      // Шаг 1: Получаем тип медиа для определения правильных метрик
      const mediaInfoUrl = `${INSTAGRAM_API_BASE}/${mediaId}?fields=media_type,media_product_type&access_token=${accessToken}`;

      const mediaInfoResponse = await fetch(mediaInfoUrl);

      if (!mediaInfoResponse.ok) {
        console.error('[Reports API] Error getting media type');
        return {};
      }

      const mediaInfo = await mediaInfoResponse.json();

      if (mediaInfo.error) {
        console.error('[Reports API] Error getting media type:', mediaInfo.error);
        return {};
      }

      media_type = mediaInfo.media_type;
      media_product_type = mediaInfo.media_product_type;
    }

    console.log('[Reports API] Media info:', { mediaId, media_product_type, media_type });
    
    // Шаг 2: Запрашиваем все доступные метрики
    // Для постов и рилсов доступны: views, total_interactions, shares, saved, reach
    // Для рилсов также доступны: crossposted_views, facebook_views (только если кросспостнуты в FB)
    let metricsToRequest = 'views,total_interactions,shares,saved,reach';
    
    // Для REELS пробуем добавить новые метрики
    const isReels = media_product_type === 'REELS';
    if (isReels) {
      metricsToRequest += ',crossposted_views,facebook_views';
    }
    
    console.log('[Reports API] Requesting metrics:', metricsToRequest);
    
    // Шаг 3: Запрашиваем метрики с period=lifetime
    let url = `${INSTAGRAM_API_BASE}/${mediaId}/insights?metric=${metricsToRequest}&period=lifetime&access_token=${accessToken}`;
    
    let response = await fetch(url);
    let data;
    
    // Парсим ответ ВСЕГДА, даже при ошибке HTTP (Instagram может вернуть 400 с деталями)
    try {
      const text = await response.text();
      data = JSON.parse(text);
    } catch (e) {
      console.error('[Reports API] Failed to parse response:', e);
      return {};
    }
    
    console.log('[Reports API] Initial response (HTTP', response.status, '):', JSON.stringify(data, null, 2));

    // Fallback #0: кросспост-метрики Instagram API отвергает ошибкой «Fatal»
    // целиком. Повторяем общий набор без них — иначе следующий шаг теряет
    // shares, и органические репосты у всех рилсов выходили нулём.
    if (data.error && isReels) {
      url = `${INSTAGRAM_API_BASE}/${mediaId}/insights?metric=views,total_interactions,shares,saved,reach&period=lifetime&access_token=${accessToken}`;
      try {
        response = await fetch(url);
        data = JSON.parse(await response.text());
      } catch (e) {
        console.error('[Reports API] Fallback #0 parse error:', e);
        return {};
      }
    }

    // Fallback #1: Если ошибка — пробуем без crosspost-метрик и без shares
    if (data.error) {
      console.warn(`[Reports API] Error on first attempt (${media_type}/${media_product_type}):`, data.error.message || data.error);
      console.log('[Reports API] Retrying with basic metrics: views,reach,saved,total_interactions...');
      
      const fallback1 = 'views,reach,saved,total_interactions';
      url = `${INSTAGRAM_API_BASE}/${mediaId}/insights?metric=${fallback1}&period=lifetime&access_token=${accessToken}`;
      
      try {
        response = await fetch(url);
        const text2 = await response.text();
        data = JSON.parse(text2);
        console.log('[Reports API] Fallback #1 response:', JSON.stringify(data, null, 2));
      } catch (e) {
        console.error('[Reports API] Fallback #1 parse error:', e);
        return {};
      }
    }
    
    // Fallback #2: Если всё ещё ошибка — пробуем только views и reach
    if (data.error) {
      console.warn('[Reports API] Fallback #1 failed, trying minimal metrics: views,reach...');
      
      const fallback2 = 'views,reach';
      url = `${INSTAGRAM_API_BASE}/${mediaId}/insights?metric=${fallback2}&period=lifetime&access_token=${accessToken}`;
      
      try {
        response = await fetch(url);
        const text3 = await response.text();
        data = JSON.parse(text3);
        console.log('[Reports API] Fallback #2 response:', JSON.stringify(data, null, 2));
      } catch (e) {
        console.error('[Reports API] Fallback #2 parse error:', e);
        return {};
      }
    }
    
    // Fallback #3: Если и это не сработало — только views
    if (data.error) {
      console.warn('[Reports API] Fallback #2 failed, trying views only...');
      
      url = `${INSTAGRAM_API_BASE}/${mediaId}/insights?metric=views&period=lifetime&access_token=${accessToken}`;
      
      try {
        response = await fetch(url);
        const text4 = await response.text();
        data = JSON.parse(text4);
        console.log('[Reports API] Fallback #3 response:', JSON.stringify(data, null, 2));
      } catch (e) {
        console.error('[Reports API] Fallback #3 parse error:', e);
        return {};
      }
    }
    
    // Если все fallback'и провалились
    if (data.error) {
      console.error('[Reports API] All fallbacks failed:', data.error);
      return {};
    }
    
    console.log('[Reports API] Raw insights data:', data);
    
    const insights: Partial<MediaInsights> = {};
    
    // Шаг 4: Парсим результат
    if (data.data && Array.isArray(data.data)) {
      data.data.forEach((metric: any) => {
        const value = metric.values?.[0]?.value;
        if (value !== undefined) {
          switch (metric.name) {
            case 'views':
              insights.views = value;
              console.log('[Reports API] Views:', value);
              break;
            case 'total_interactions':
              insights.total_interactions = value;
              console.log('[Reports API] Total Interactions:', value);
              break;
            case 'shares':
              insights.shares = value;
              console.log('[Reports API] Shares:', value);
              break;
            case 'saved':
              insights.saved = value;
              console.log('[Reports API] Saved:', value);
              break;
            case 'reach':
              insights.reach = value;
              console.log('[Reports API] Reach:', value);
              break;
            case 'crossposted_views':
              insights.crossposted_views = value;
              console.log('[Reports API] Crossposted Views:', value);
              break;
            case 'facebook_views':
              insights.facebook_views = value;
              console.log('[Reports API] Facebook Views:', value);
              break;
          }
        }
      });
    }
    
    console.log('[Reports API] Final insights:', insights);
    
    return insights;
  } catch (error) {
    console.error('[Reports API] Error getting insights:', error);
    return {};
  }
}

/**
 * Получает mediaId из permalink
 */
export async function getMediaIdFromPermalink(
  accessToken: string,
  permalink: string,
  userId?: string
): Promise<string> {
  try {
    // Если userId не передан, получаем его через /me
    let finalUserId = userId;
    if (!finalUserId) {
      console.log('🔍 Getting user ID from /me endpoint...');
      const meUrl = `${INSTAGRAM_API_BASE}/me?fields=id,username&access_token=${accessToken}`;
      const meResponse = await fetch(meUrl);
      
      // ПРОВЕРКА НА ПУСТОЙ ОТВЕТ
      const meText = await meResponse.text();
      if (!meText) {
        throw new Error('Empty response from Instagram API');
      }
      
      const meData = JSON.parse(meText);
      
      if (meData.error) {
        console.error('❌ Error getting user ID:', meData.error);
        throw new Error(meData.error.message);
      }
      
      finalUserId = meData.id;
      console.log('✅ Got user ID:', finalUserId, 'Username:', meData.username);
    }
    
    const normalizedPermalink = normalizePermalink(permalink);
    console.log('🔍 Looking for normalized permalink:', normalizedPermalink);
    
    // Используем userId ля плучения медиа С ПАГИНАЦИЕЙ
    let url: string | null = `${INSTAGRAM_API_BASE}/${finalUserId}/media?fields=id,permalink&limit=100&access_token=${accessToken}`;
    let totalChecked = 0;
    let pageNumber = 1;
    
    while (url) {
      console.log(`🔍 Fetching page ${pageNumber}...`);
      
      const response = await fetch(url);
      
      // ПРОВЕРКА НА ПУСТОЙ ОТВЕТ
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API error:', errorText);
        throw new Error(`Instagram API error: ${response.status} ${response.statusText}`);
      }
      
      const text = await response.text();
      if (!text) {
        console.error('❌ Empty response from Instagram API');
        break;
      }
      
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('❌ Failed to parse JSON:', text);
        throw new Error('Некорректный отве от Instagram API');
      }
      
      if (data.error) {
        console.error('❌ Error getting media list:', data.error);
        throw new Error(data.error.message);
      }
      
      if (!data.data || data.data.length === 0) {
        console.error('❌ No more media found');
        break;
      }
      
      console.log(`✅ Got ${data.data.length} media items on page ${pageNumber}`);
      totalChecked += data.data.length;
      
      // Ищем нужный permalink на текущей странице
      const media = data.data.find((m: any) => {
        const normalized = normalizePermalink(m.permalink);
        return normalized === normalizedPermalink;
      });
      
      if (media) {
        console.log(`✅ Found media ID: ${media.id} (checked ${totalChecked} total posts)`);
        return media.id;
      }
      
      // Переходим к следующей странице
      url = data.paging?.next || null;
      pageNumber++;
      
      // Защита от бесконечного цикла - максимум 10 страниц (1000 постов)
      if (pageNumber > 10) {
        console.warn('⚠️ Reached maximum pagination limit (10 pages)');
        break;
      }
    }
    
    // Если не найдено после всех страниц
    console.error(`❌ Media not found after checking ${totalChecked} posts`);
    throw new Error(`Медиа не найдено среди последних ${totalChecked} постов аккаунта`);
  } catch (error) {
    console.error('❌ Error getting media ID from permalink:', error);
    throw error;
  }
}

/**
 * 🆕 FACEBOOK GRAPH API - Получает статистику напрямую через media-id
 * Как на скриншоте: /{media-id}?fields=...&access_token={facebook-token}
 */
export async function getMediaStatsFacebook(
  facebookAccessToken: string,
  permalink: string
): Promise<any> {
  try {
    console.log('[FB GRAPH] Starting Facebook Graph API experiment for:', permalink);
    
    // Шаг 1: Извлекаем username из permalink
    const username = extractUsernameFromPermalink(permalink);
    if (!username) {
      throw new Error('Не удалось извлечь username из permalink');
    }
    
    console.log('[FB GRAPH] Username:', username);
    
    // Шаг 2: Получаем Instagram Business Account ID через Facebook Pages
    console.log('[FB GRAPH] Step 1: Getting Instagram Business Account ID from Facebook Pages...');
    const pageUrl = `https://graph.facebook.com/v21.0/me/accounts?fields=instagram_business_account&access_token=${facebookAccessToken}`;
    const pageResponse = await fetch(pageUrl);
    const pageData = await pageResponse.json();
    
    if (pageData.error) {
      console.error('[FB GRAPH] Error getting page info:', pageData.error);
      throw new Error(pageData.error.message);
    }
    
    console.log('[FB GRAPH] Page data:', pageData);
    
    if (!pageData.data || pageData.data.length === 0) {
      throw new Error('Не найдено Facebook Pages с подключенным Instagram Business Account');
    }
    
    const instagramBusinessAccountId = pageData.data[0]?.instagram_business_account?.id;
    
    if (!instagramBusinessAccountId) {
      throw new Error('Instagram Business Account не найден в Facebook Page');
    }
    
    console.log('[FB GRAPH] Instagram Business Account ID:', instagramBusinessAccountId);
    
    // Шаг 3: Используем Business Discovery API для получения медиа
    console.log('[FB GRAPH] Step 2: Using Business Discovery API to get media...');
    
    // Business Discovery API запрос
    // GET /{ig-user-id}?fields=business_discovery.username(target_username){media{id,permalink,like_count,comments_count,view_count,timestamp}}
    const fields = `business_discovery.username(${username}){media.limit(1000){id,permalink,like_count,comments_count,view_count,timestamp,media_type,media_url,caption}}`;
    const discoveryUrl = `https://graph.facebook.com/v21.0/${instagramBusinessAccountId}?fields=${fields}&access_token=${facebookAccessToken}`;
    
    console.log('[FB GRAPH] Discovery URL:', discoveryUrl);
    
    const discoveryResponse = await fetch(discoveryUrl);
    const discoveryData = await discoveryResponse.json();
    
    if (discoveryData.error) {
      console.error('[FB GRAPH] Error in Business Discovery API:', discoveryData.error);
      throw new Error(discoveryData.error.message);
    }
    
    console.log('[FB GRAPH] Business Discovery response:', discoveryData);
    
    const mediaList = discoveryData.business_discovery?.media?.data;
    
    if (!mediaList || mediaList.length === 0) {
      throw new Error('Медиа не найдено через Business Discovery API');
    }
    
    // Шаг 4: Находим нужный пост по permalink
    const normalizedPermalink = normalizePermalink(permalink);
    console.log('[FB GRAPH] Looking for normalized permalink:', normalizedPermalink);
    
    const mediaItem = mediaList.find((m: any) => {
      const normalized = normalizePermalink(m.permalink);
      return normalized === normalizedPermalink;
    });
    
    if (!mediaItem) {
      throw new Error('Медиа не найдено среди последних постов');
    }
    
    const mediaId = mediaItem.id;
    console.log('[FB GRAPH] Found media ID:', mediaId);
    console.log('[FB GRAPH] ✅ SUCCESS! Media stats:', JSON.stringify(mediaItem, null, 2));
    
    return {
      mediaId,
      permalink,
      facebookGraphApi: mediaItem,
      summary: {
        likes: mediaItem.like_count,
        comments: mediaItem.comments_count,
        views: mediaItem.view_count,
        media_type: mediaItem.media_type,
        timestamp: mediaItem.timestamp,
      }
    };
  } catch (error) {
    console.error('[FB GRAPH] Error:', error);
    throw error;
  }
}

/**
 * 🧪 ЭКСПЕРИМЕНТАЛЬНАЯ ФУНКЦИЯ
 * Пробуем получить view_count напрямую через Media ID
 * GET https://graph.facebook.com/v24.0/{MEDIA_ID}?fields=view_count,like_count,comments_count
 */
export async function getAllInsightsExperimental(
  accessToken: string,
  permalink: string
): Promise<any> {
  try {
    console.log('[EXPERIMENT] Starting direct Media ID experiment for:', permalink);
    
    // Шаг 1: Получаем mediaId
    const mediaId = await getMediaIdFromPermalink(accessToken, permalink);
    console.log('[EXPERIMENT] Media ID:', mediaId);
    
    const results: any = {
      mediaId,
      tests: []
    };
    
    // Шаг 2: Тест #1 - Facebook Graph API с view_count через Media ID
    const facebookUrl = `https://graph.facebook.com/v24.0/${mediaId}?fields=view_count,like_count,comments_count,media_type,media_url,timestamp,permalink&access_token=${accessToken}`;
    console.log('[EXPERIMENT] Test #1 - Facebook Graph API (direct media ID) with view_count:', facebookUrl);
    
    try {
      const response1 = await fetch(facebookUrl);
      const data1 = await response1.json();
      
      results.tests.push({
        test: 'Facebook Graph API (direct media ID) with view_count',
        api: 'graph.facebook.com',
        url: facebookUrl,
        success: !data1.error,
        response: data1
      });
      
      console.log('[EXPERIMENT] Test #1 response:', JSON.stringify(data1, null, 2));
    } catch (error) {
      results.tests.push({
        test: 'Facebook Graph API (direct media ID) with view_count',
        api: 'graph.facebook.com',
        url: facebookUrl,
        success: false,
        error: String(error)
      });
      console.error('[EXPERIMENT] Test #1 error:', error);
    }
    
    // Шаг 3: Тест #2 - Instagram Graph API с view_count через Media ID (для сравнения)
    const instagramUrl = `${INSTAGRAM_API_BASE}/${mediaId}?fields=view_count,like_count,comments_count,media_type,media_url,timestamp,permalink&access_token=${accessToken}`;
    console.log('[EXPERIMENT] Test #2 - Instagram Graph API (direct media ID) with view_count:', instagramUrl);
    
    try {
      const response2 = await fetch(instagramUrl);
      const data2 = await response2.json();
      
      results.tests.push({
        test: 'Instagram Graph API (direct media ID) with view_count',
        api: 'graph.instagram.com',
        url: instagramUrl,
        success: !data2.error,
        response: data2
      });
      
      console.log('[EXPERIMENT] Test #2 response:', JSON.stringify(data2, null, 2));
    } catch (error) {
      results.tests.push({
        test: 'Instagram Graph API (direct media ID) with view_count',
        api: 'graph.instagram.com',
        url: instagramUrl,
        success: false,
        error: String(error)
      });
      console.error('[EXPERIMENT] Test #2 error:', error);
    }
    
    // Шаг 4: Тест #3 - Facebook Graph API БЕЗ view_count (контроль)
    const facebookUrlControl = `https://graph.facebook.com/v24.0/${mediaId}?fields=like_count,comments_count,media_type,media_url,timestamp,permalink&access_token=${accessToken}`;
    console.log('[EXPERIMENT] Test #3 - Facebook Graph API WITHOUT view_count (control):', facebookUrlControl);
    
    try {
      const response3 = await fetch(facebookUrlControl);
      const data3 = await response3.json();
      
      results.tests.push({
        test: 'Facebook Graph API WITHOUT view_count (control)',
        api: 'graph.facebook.com',
        url: facebookUrlControl,
        success: !data3.error,
        response: data3
      });
      
      console.log('[EXPERIMENT] Test #3 response:', JSON.stringify(data3, null, 2));
    } catch (error) {
      results.tests.push({
        test: 'Facebook Graph API WITHOUT view_count (control)',
        api: 'graph.facebook.com',
        url: facebookUrlControl,
        success: false,
        error: String(error)
      });
      console.error('[EXPERIMENT] Test #3 error:', error);
    }
    
    console.log('[EXPERIMENT] FINAL RESULTS:', JSON.stringify(results, null, 2));
    
    return results;
  } catch (error) {
    console.error('[EXPERIMENT] Error:', error);
    throw error;
  }
}

/**
 * Получает insights для конкретного поста/рилса
 */
export async function getMediaInsightsForPost(
  accessToken: string,
  mediaId: string
): Promise<Partial<MediaInsights>> {
  try {
    // Шаг 1: Получаем тип медиа для опрееления правильных метрик
    const mediaInfoUrl = `${INSTAGRAM_API_BASE}/${mediaId}?fields=media_type,media_product_type&access_token=${accessToken}`;
    
    const mediaInfoResponse = await fetch(mediaInfoUrl);
    
    if (!mediaInfoResponse.ok) {
      console.error('[Reports API] Error getting media type');
      return {};
    }
    
    const mediaInfo = await mediaInfoResponse.json();
    
    if (mediaInfo.error) {
      console.error('[Reports API] Error getting media type:', mediaInfo.error);
      return {};
    }
    
    const { media_type, media_product_type } = mediaInfo;
    
    console.log('[Reports API] Media info:', { mediaId, media_product_type, media_type });
    console.log('[Reports API] Full mediaInfo response:', JSON.stringify(mediaInfo, null, 2));
    
    // Шаг 2: Используем views для ВСЕХ типов (FEED, STORY, REELS)
    // Согласно документации Instagram API v22.0+, impressions упразднена
    const metricsToRequest = 'views';
    
    console.log('[Reports API] Requesting metrics:', metricsToRequest);
    
    // Шаг 3: Запрашиваем метрики с period=lifetime
    const url = `${INSTAGRAM_API_BASE}/${mediaId}/insights?metric=${metricsToRequest}&period=lifetime&access_token=${accessToken}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Reports API] Insights request failed:', errorText);
      return {};
    }
    
    const data = await response.json();
    
    if (data.error) {
      console.error('[Reports API] Insights error:', data.error);
      return {};
    }
    
    console.log('[Reports API] Raw insights data:', data);
    
    const insights: Partial<MediaInsights> = {};
    
    // Шаг 4: Парсим результа��
    if (data.data && Array.isArray(data.data)) {
      data.data.forEach((metric: any) => {
        const value = metric.values?.[0]?.value;
        if (value !== undefined && metric.name === 'views') {
          insights.views = value;
          console.log('[Reports API] Views:', value);
        }
      });
    }
    
    console.log('[Reports API] Final insights:', insights);
    
    return insights;
  } catch (error) {
    console.error('[Reports API] Error getting insights:', error);
    return {};
  }
}

/**
 * Получает базовую информацию о media по permalink
 */
export async function getMediaByPermalink(
  accessToken: string,
  allMedia: InstagramMedia[],
  permalink: string
): Promise<InstagramMedia | null> {
  // Находим media по permalink из уже загруженных
  return allMedia.find(m => m.permalink === permalink) || null;
}

/**
 * Получает комментарии и лайки напрямую из media object
 */
export async function getMediaDetails(
  accessToken: string,
  mediaId: string
): Promise<{ comments?: number; likes?: number }> {
  try {
    const url = `${INSTAGRAM_API_BASE}/${mediaId}?fields=like_count,comments_count&access_token=${accessToken}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      return {};
    }
    
    const data = await response.json();
    
    return {
      comments: data.comments_count,
      likes: data.like_count
    };
  } catch (error) {
    console.error('Error getting media details:', error);
    return {};
  }
}